import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import './ClevrStudio.css';

interface SolutionDetail {
  title: string;
  overview: string;
  introduction: {
    title: string;
    description: string;
  }[];
}

const ClevrStudio: React.FC = () => {
  const navigate = useNavigate();
  const [currentVideo, setCurrentVideo] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSolution, setSelectedSolution] = useState<SolutionDetail | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Reload video when currentVideo changes
    if (videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(err => console.log('Video play failed:', err));
    }
  }, [currentVideo]);

  const videoSections = [
    {
      id: 'overview',
      url: 'https://www.aqara.com/wp-content/uploads/2025/01/studiobanvideo0010104-1.mp4',
      title: 'CLEVR Studio',
      subtitle: 'Overview',
      description: 'Experience the future of smart living with CLEVR\'s innovative automation solutions',
      icon: '🏢'
    },
    {
      id: 'office-buildings',
      url: 'https://www.aqara.com/wp-content/uploads/2025/01/studiobanvideo0020104.mp4',
      title: 'Office Buildings',
      subtitle: 'Smart Workplace Solutions',
      description: 'Transform your office with intelligent automation that enhances productivity and efficiency',
      icon: '🏢'
    },
    {
      id: 'hotels-restaurants',
      url: 'https://www.aqara.com/wp-content/uploads/2025/01/studiobanvideo0030104.mp4',
      title: 'Hotels & Restaurants',
      subtitle: 'Hospitality Excellence',
      description: 'Create memorable guest experiences with seamless smart hospitality solutions',
      icon: '🏨'
    },
    {
      id: 'hospitals',
      url: 'https://www.aqara.com/wp-content/uploads/2025/01/studiobanvideo0040104.mp4',
      title: 'Hospitals',
      subtitle: 'Healthcare Innovation',
      description: 'Advanced smart solutions for modern healthcare facilities and patient care',
      icon: '🏥'
    },
    {
      id: 'smart-homes',
      url: 'https://www.aqara.com/wp-content/uploads/2025/01/studiobanvideo005.mp4',
      title: 'Smart Homes',
      subtitle: 'Intelligent Living',
      description: 'Transform your home into a smart sanctuary with cutting-edge automation',
      icon: '🏠'
    },
    {
      id: 'schools',
      url: 'https://www.aqara.com/wp-content/uploads/2025/01/studiobanvideo0060104.mp4',
      title: 'Schools',
      subtitle: 'Educational Excellence',
      description: 'Smart solutions that create better learning environments for students and educators',
      icon: '🏫'
    }
  ];

  const handleCategoryClick = (index: number) => {
    setCurrentVideo(index);
  };

  const solutionDetails: { [key: string]: SolutionDetail } = {
    'Smart Office': {
      title: 'Smart Office',
      overview: 'Aqara smart office solution provides enterprises with a complete solution for office buildings. Based on the Aqara IoT system and smart office SaaS applications, it provides digital services for enterprise operations. With IoT devices, it can help manage and control lighting, HVAC, shading, water and electricity security, providing environment monitoring, office energy consumption management, security management and real-time message push services, etc. It also provides customizable scenes for different areas to provide a comfortable, safe and healthy office environment while saving energy, and improving the work efficiency of employees.\n\nAdditionally, with Aqara\'s capabilities in big data and AI technology, it can implement space management, hierarchical partition access control, AI smart scenes, AI entrance/exit control, AI attendance, AI pedestrian trajectory monitoring, and meeting room reservation management.\n\nWith our powerful data integration and automated processing capabilities, enterprises can achieve data-driven energy conservation, significantly cut energy usage and operational expenses, save energy, and minimize carbon emissions.',
      introduction: [
        {
          title: 'Smart access control',
          description: 'It makes access control and attendance more convenient, ensuring safety and efficiency.\n• Fast Pass\n• Automatic Clock-in/out\n• Suspicious Person Detection\n• Risk Warning\n• Smart Welcome\n• Unmanned Security Guard'
        },
        {
          title: 'Smart meeting',
          description: 'Never delay any important meeting.\n• Check-in with Face Recognition\n• Timeout Warning\n• Resource Release\n• Participation Reminder\n• Projection Scene\n• Schedule Display'
        },
        {
          title: 'Smart workstation',
          description: 'Provides better office experience and improves work efficiency.\n• Temperature Adjustment\n• Smart Reminder\n• Sedentary Warning\n• Office Mode\n• Lunch Break Mode\n• Off Work Mode'
        },
        {
          title: 'Smart administration',
          description: 'It helps with data visualization, smart decision-making, energy saving and efficiency enhancement, and security protection.\n• Attendance Analysis\n• Energy Consumption Control\n• Auxiliary Decision-Making\n• Suspicious Person Warning\n• Fire Warning\n• One-button Arming'
        }
      ]
    },
    'Smart Community': {
      title: 'Smart Community',
      overview: 'The Aqara smart community solution can carry out functions such as unobtrusive access control, holistic smart home automation, smart monitoring, and linkage between home and community security, making life in the community more convenient, management more efficient, and service more thoughtful.',
      introduction: [
        {
          title: 'Unobtrusive access control',
          description: 'Users can enter the community and home with face recognition at the community entrance, elevator, and door lock at home, making access more convenient.'
        },
        {
          title: 'Smart monitoring',
          description: 'The property management center can use AI to analyze the surveillance footage for features like face control, trajectory query, behavior analysis, smart alert, etc. to ensure the safety of community members 24/7 and cut down on staff costs associated with property security and maintenance.'
        },
        {
          title: 'Smart home',
          description: 'With abundant scene modes, automations and multiple control methods, it can utilize smart control of lighting, curtains, air conditioners and other appliances in the home, and protect family and property safety through smart security solutions.\n\nWith the apartment owner\'s authorization, water, electricity and gas can be connected to the community which protects the security and safety of the residents in the community.'
        }
      ]
    },
    'Smart Elderly Care': {
      title: 'Smart Elderly Care',
      overview: 'With Zigbee technology, the Aqara IoT Platform, big data and AI technology, Aqara smart elderly care solution helps support and manage the daily life of the elderly including health and safety, life convenience, entertainment and leisure and other aspects.\n\nA friendly, autonomous, and customized interaction for the elderly is made possible by the automatic monitoring, early warning, and even active management of information pertaining to seniors. This technology also supports the provision of home-based elderly care services to achieve socially intelligent elderly care services in partnership with communities and elderly care institutions.',
      introduction: [
        {
          title: 'Create a comfortable and healthy living environment',
          description: 'Improving the air, water, light and noise living environment of the elderly.'
        },
        {
          title: 'Make daily life more convenient',
          description: 'Assisting the elderly in daily life care such as transportation, bathing, etc.'
        },
        {
          title: '24hr care and AI security',
          description: 'Home security is integrated with 24 hour home health and friendly reminders to offer a guarantee of safety and comfort.'
        },
        {
          title: 'Help you enjoy the care and companionship of children all the time',
          description: 'The time and spatial distance between the elderly and their children is reduced by digital management and remote connectivity.'
        },
        {
          title: 'Customizable based on your habits',
          description: 'The solution can enrich the senior life scenario experience, inheriting seniors\' frugal habits to help achieve carbon neutrality.'
        },
        {
          title: 'Enjoy care of the whole society at home',
          description: 'The open system enables multi-platform integration among home, community and medical institutions, and the whole society can work together to provide services for the elderly.'
        }
      ]
    },
    'Smart Hotel': {
      title: 'Smart Hotel',
      overview: 'Aqara smart hotel solution is suitable for hotels, homestays, etc. With the Aqara smart hotel system and smart devices, we provide AIoT integrated hotel smart services for different types of hotels, creating an immersive and comprehensive smart space experience for hotel guests, and providing smart service for hotel guests throughout the stay, giving guests a more convenient check-in experience while improving the hotel service quality and reducing hotel operating costs.',
      introduction: [
        {
          title: 'Functions',
          description: '(1) Intelligentization of the entire check-in process: Online reservations and payment, smart check-ins, elevator control with facial recognition, voice customer control/demand, robot deliveries, one-click check-out and invoicing.\n\n(2) Smart room control: welcome speech, music control, movie control, lighting control, temperature control, curtain control, smart bathroom, smart door locking, sleep monitoring, scene customization, voice customer demand, customer control mini program, etc.'
        },
        {
          title: 'Values',
          description: '(1) Improves hotel image: It helps upgrade the hotel service and build up a brand new image for hotels.\n\n(2) Low retrofit cost: The wireless technology saves the cost of physical wires.\n\n(3) Improve user experience: Automatically adjust indoor lighting, curtains, temperature, etc., so that guests can always enjoy a comfortable environment.\n\n(4) Reduce energy consumption: On average, lighting and central air-conditioning consume 70% of the energy in a project, and the smart hotel solution can help reduce energy usage by 30%.\n\n(5) More reliable and stable solution: With the software and hardware integrated solution, it provides users with an IoT platform that is more powerful, cost-effective and stable.\n\n(6) Improve operation efficiency: It helps improve the efficiency through automatic control, centralized control, and simplified operation; functions such as remote and background management improve management efficiency and reduce labor costs.'
        }
      ]
    }
  };

  const solutions = [
    { id: 'smart-office', title: 'Smart Office', image: 'https://www.aqara.com/wp-content/uploads/2023/03/%E5%9B%BE%E7%89%87-5-1.png', description: 'CLEVR transforms office environments with intelligent automation, energy management, and seamless control of lighting, climate, security, and workplace productivity systems.' },
    { id: 'smart-community', title: 'Smart Community', image: 'https://www.aqara.com/wp-content/uploads/2023/03/Smart-Community.png.webp', description: 'CLEVR can help community residents, facility managers, or security services perform functions, such as multimedia access control, remote control, lighting, smart panels, and more.' },
    { id: 'smart-elderly-care', title: 'Smart Elderly Care', image: 'https://www.aqara.com/wp-content/uploads/2023/03/Smart-Elderly-Care.png.webp', description: 'CLEVR can provide homes for the elderly big data and AI technology; higher price efficient care solution helps our medical facilities, to offer safer care.' },
    { id: 'smart-hotel', title: 'Smart Hotel', image: 'https://www.aqara.com/wp-content/uploads/2023/03/%E5%9B%BE%E7%89%87-6-1.png', description: 'Aqara smart hotel solution is suitable for hotels, homestays, etc. With the Aqara smart hotel system and smart devices, we provide AIoT integrated hotel smart services.' }
  ];

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => Math.max(0, prev - 1));
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => Math.min(solutions.length - 3, prev + 1));
  };

  const handleLearnMoreClick = (solutionTitle: string) => {
    setSelectedSolution(solutionDetails[solutionTitle]);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSolution(null);
    document.body.style.overflow = 'auto';
  };

  return (
    <div className="clevr-studio-page">
      <Header />
      
      {/* Single Hero Section with Dynamic Video */}
      <section className="hero-video-section">
        <div className="video-background">
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            className="background-video"
            key={currentVideo}
          >
            <source src={videoSections[currentVideo].url} type="video/mp4" />
          </video>
          <div className="video-overlay"></div>
        </div>
        
        {/* Category Navigation */}
        <nav className="category-navigation">
          {videoSections.map((section, index) => (
            <button
              key={index}
              className={`nav-button ${currentVideo === index ? 'active' : ''}`}
              onClick={() => handleCategoryClick(index)}
            >
              <span className="nav-icon">{section.icon}</span>
              <span className="nav-label">{section.title}</span>
            </button>
          ))}
        </nav>
        
        {/* Video Content */}
        <div className="video-content">
          <div className="content-wrapper">
            <h1 className="section-title">{videoSections[currentVideo].title}</h1>
            <h2 className="section-subtitle">{videoSections[currentVideo].subtitle}</h2>
            <p className="section-description">{videoSections[currentVideo].description}</p>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="info-section">
        <div className="info-container">
          <h2 className="info-title">Why Choose CLEVR Studio?</h2>
          <div className="info-grid">
            <div className="info-card">
              <div className="info-icon">🎯</div>
              <h3>Precision Design</h3>
              <p>Every detail crafted to perfection for seamless integration</p>
            </div>
            <div className="info-card">
              <div className="info-icon">🚀</div>
              <h3>Advanced Technology</h3>
              <p>Cutting-edge solutions powered by the latest innovations</p>
            </div>
            <div className="info-card">
              <div className="info-icon">🔒</div>
              <h3>Secure & Reliable</h3>
              <p>Enterprise-grade security protecting your smart home</p>
            </div>
            <div className="info-card">
              <div className="info-icon">💡</div>
              <h3>Intelligent Automation</h3>
              <p>Smart systems that learn and adapt to your preferences</p>
            </div>
          </div>
        </div>
      </section>

      {/* Leading Industrial Solutions Section */}
      <section className="solutions-industrial">
        <div className="solutions-container">
          <h2 className="solutions-section-title">Leading Industrial Solutions</h2>
          <div className="solutions-carousel-container">
            <button
              className="carousel-arrow carousel-arrow-left"
              onClick={handlePrevSlide}
              style={{ visibility: currentSlide === 0 ? 'hidden' : 'visible' }}
            >
              ‹
            </button>
            <div className="solutions-cards">
              {solutions.slice(currentSlide, currentSlide + 3).map((solution) => (
                <div key={solution.id} className="solutions-card">
                  <div className="solutions-card-image-industrial">
                    <img src={solution.image} alt={solution.title} />
                  </div>
                  <h3 className="solutions-card-title">{solution.title}</h3>
                  <p className="solutions-card-text">{solution.description}</p>
                  <button className="solutions-card-btn" onClick={() => handleLearnMoreClick(solution.title)}>Learn More</button>
                </div>
              ))}
            </div>
            <button
              className="carousel-arrow carousel-arrow-right"
              onClick={handleNextSlide}
              style={{ visibility: currentSlide >= solutions.length - 3 ? 'hidden' : 'visible' }}
            >
              ›
            </button>
          </div>
        </div>
      </section>

      {/* Modal for Solution Details */}
      {isModalOpen && selectedSolution && (
        <div className="solution-modal-overlay" onClick={handleCloseModal}>
          <div className="solution-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="solution-modal-close" onClick={handleCloseModal}>×</button>
            <h2 className="solution-modal-title">{selectedSolution.title}</h2>
            <div className="solution-modal-section">
              <h3 className="solution-modal-section-title">Overview</h3>
              <p className="solution-modal-text">{selectedSolution.overview}</p>
            </div>
            <div className="solution-modal-section">
              <h3 className="solution-modal-section-title">Introduction</h3>
              <div className="solution-modal-intro-list">
                {selectedSolution.introduction.map((item, index) => (
                  <div key={index} className="solution-modal-intro-item">
                    <h4 className="solution-modal-intro-title">{item.title}</h4>
                    <p className="solution-modal-intro-text">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ClevrStudio;
